<script setup lang="ts">
    import { computed } from 'vue';
    import ThousandSeparatorInput from '@/components/ThousandSeparatorInput.vue';
    import type { Row, Table } from '@tanstack/vue-table';
    import type { VatSubmission } from '@/types';
    import { useI18n } from 'vue-i18n';
    import { formatCurrency } from '@/utils/helpers';
    import { useVatStore } from '@/stores/vat';

    const { t } = useI18n();

    const vatStore = useVatStore();

    const props = defineProps<{
        table: Table<VatSubmission>;
        row: Row<VatSubmission>;
        columnId: keyof VatSubmission;
    }>();

    const model = defineModel<number | null>();

    const isDeadlinePassed = computed(() => {
        if (!props.row.original.deadline) {
            return false;
        }

        const deadline = new Date(props.row.original.deadline);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        deadline.setHours(0, 0, 0, 0);

        return deadline < today;
    });

    const isDisabled = computed(() => {
        return (
            isDeadlinePassed.value ||
            (props.row.original.status !== 'draft' && !props.row.original._is_editing)
        );
    });

    const handleBlur = () => {
        if (!isDisabled.value) {
            props.table.options.meta?.updateData?.(props.row, props.columnId, model.value);
        }
    };
</script>

<template>
    <div class="flex flex-col gap-1">
        <div class="relative flex items-center">
            <ThousandSeparatorInput
                v-model="model"
                with-decimals
                :placeholder="t('vat.specify_amount')"
                class="min-w-52 pr-8"
                @blur="handleBlur"
                :disabled="isDisabled"
            />
            <span class="pointer-events-none absolute right-2">Kr.</span>
        </div>
        <span
            v-if="vatStore.showPreviousSubmission && row.original.previous_submission"
            class="text-faded-foreground flex items-center justify-between gap-1 text-[10px]"
        >
            <span>
                {{ formatCurrency(row.original.previous_submission[props.columnId] as number) }}
            </span>
            <span>{{ t('vat.previous_submission') }}</span>
        </span>
    </div>
</template>
